import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import { CheckCircleFilled, HistoryOutlined } from "@ant-design/icons";
import { SbTable, SbTag, SbButton, SbTooltip } from "uiCore";
import SkillGapAccordion from "../components/SkillGapAccordion";
import CriticalSkillTags from "../components/PrioritySkillTags";
import ShortlistTag from "../components/ShortlistTag";
import { getScoreColor } from "Utils/scoreColor.utils";
import { buildScoreColumn } from "./RedeploymentCenterV2.utils";
import { useNextRolesForPerson, useSkillGapForPair } from "../useTalentMobilityData";
import { getCriticalSkillGaps } from "../mockData/talentMobility.mock";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import { useTargetedRoleIdsForEmployee } from "Store/useRedeploymentPlanStore";
import { NestedTableWrapperStyled } from "./RedeploymentCenterV2.styles";
import { NESTED_TABLE_SCROLL_HEIGHT } from "../talentMobility.constants";

const { Text } = Typography;

// The nested table Employee View's top-level rows expand into — candidate
// roles a person could move into (person → roles), with a click-to-toggle
// skill-gap leaf panel. (Role View's counterpart, RoleCandidatesTable, was
// removed along with its last caller: API role rows carry an empty
// `candidates` array — per-role successor lists live behind
// role-successor-analytics, which needs an occupant user id Role View
// doesn't have — so non-reducing Role View rows show the reduction-required
// warning instead of expanding inline. Restoring per-role candidate
// browsing needs that data gap closed first.)

const useExpandedKeys = () => {
  const [expandedKeys, setExpandedKeys] = useState([]);
  const toggleKey = useCallback(
    (key) =>
      setExpandedKeys((previous) =>
        previous.includes(key) ? previous.filter((k) => k !== key) : [...previous, key]
      ),
    []
  );
  return { expandedKeys, toggleKey };
};

// Shared leaf: skill gap for one (role, person) pair — direction-agnostic.
// `currentRoleId` (Employee View only) keeps the accordion's "View Detailed
// Profile" link on the redeployment draft path (?current_role=...), same as
// the row-level View Profile button — without it the opened profile's
// Shortlist would POST /talent-mobility/shortlist under the signed-in
// user's own row, which /role-successor-analytics never reads.
const SkillGapForPair = ({ personId, roleId, sri, currentRoleId }) => {
  const { skillGap, isLoading } = useSkillGapForPair(roleId, personId);
  return (
    <SkillGapAccordion
      title="Skill Gap vs. This Role"
      matchPct={sri}
      skillGap={skillGap}
      roleId={roleId}
      personId={personId}
      currentRoleJdId={currentRoleId}
      loading={isLoading}
      skillLimit={5}
    />
  );
};

// Employee View's nested table — roles a person could move into. Mock people
// render their precomputed rows; API people fetch POST
// /talent-mobility/next-roles the moment this panel mounts (i.e. on expand).
export const PersonNextRolesTable = ({ person }) => {
  const { expandedKeys, toggleKey } = useExpandedKeys();
  const { nextRoles, isLoading } = useNextRolesForPerson(person);
  const thresholds = useMatchThresholds();
  const navigate = useNavigate();
  // Availability is scoped per current role (the plan store's
  // current_role_jd_sb_unique_id key); the person-level flag still feeds
  // Workforce Planning. The Shortlist for Redeployment / Undo Shortlist
  // toggle itself lives in PersonAvailabilityButton, rendered as its own
  // column on the outer people table (see useRedeploymentPeopleColumns), not
  // in this expanded panel.
  const targetedRoleIds = useTargetedRoleIdsForEmployee(person.currentRoleId, person.id);

  const columns = [
    {
      title: "Potential Next Role",
      key: "roleTitle",
      // The per-role tag marks roles added to this person's
      // targeted_jd_sb_unique_ids (via the profile page's Mark as
      // Available) — distinct from the panel-level tag below the table,
      // which is person-level availability.
      render: (_, record) => (
        <>
          {record.role?.isPast && (
            <SbTooltip content="This employee previously held this role — the assignment has ended.">
              <SbTag color="gold" icon={<HistoryOutlined />} style={{ marginRight: 6, fontWeight: 600 }}>
                Past Role
              </SbTag>
            </SbTooltip>
          )}
          {record.role?.title}
          <ShortlistTag roleId={record.roleId} personId={person.id} />
          {targetedRoleIds.has(record.roleId) && (
            <SbTag color="success" icon={<CheckCircleFilled />} style={{ marginLeft: 6 }}>
              Recommended
            </SbTag>
          )}
        </>
      ),
    },
    { title: "Function", dataIndex: ["role", "function"] },
    {
      title: "Critical Skills Gaps",
      key: "criticalSkills",
      // API rows carry criticalGaps from the response; mock rows fall back to
      // the mock lookup — same split as Role Detail's candidate table.
      render: (_, record) => (
        <CriticalSkillTags criticalGaps={record.criticalGaps ?? getCriticalSkillGaps(record.roleId, person.id)} />
      ),
    },
    buildScoreColumn("SMI Match", "sri", thresholds, {
      defaultSortOrder: "descend",
      // align: "center" (buildScoreColumn's default) only centers the body
      // cells — the sortable header's title+icon group needs this class too
      // (see RedeploymentCenterV2.styles.js).
      onHeaderCell: () => ({ className: "tm-col-header-center" }),
      // Overrides buildScoreColumn's default render — a null sri (this
      // person hasn't updated their profile against this role yet) reads as
      // an actual rock-bottom match if shown as "0%", so show the same
      // dash + explanatory tooltip RoleDetailV2's own SMI column falls back
      // to for its equivalent case, instead of a percentage.
      render: (sri) =>
        sri == null ? (
          <SbTooltip content="User has not updated profile">—</SbTooltip>
        ) : (
          <SbTag color={getScoreColor(sri, thresholds)}>{sri}%</SbTag>
        ),
    }),
    {
      title: "",
      key: "viewProfile",
      align: "center",
      render: (_, record) => (
        <SbButton
          buttonType="secondary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            // current_role tells the Candidate Profile's Shortlist which
            // redeployment plan to append this (person, role) pair to;
            // origin marks the people-first flow, so the profile's Mark as
            // Available also decrements the current role's target (profiles
            // entered from the target-first Reduction Detail don't).
            navigate(
              `/talent-mobility/candidate/${person.id}/role/${record.roleId}?index=smi&current_role=${person.currentRoleId}&origin=employee-view`
            );
          }}
        >
          View Profile
        </SbButton>
      ),
    },
  ];

  return (
    <NestedTableWrapperStyled>
      <div className="nested-title">Roles {person.name} could move into next</div>
      {isLoading || nextRoles.length ? (
        <SbTable
          columns={columns}
          dataSource={nextRoles}
          loading={isLoading}
          rowKey={(record) => record.roleId}
          pagination={false}
          // No row cap here (unlike AtRiskDetailPanel's top-3 slice) — see
          // NESTED_TABLE_SCROLL_HEIGHT for why this needs a fixed scroll height.
          scroll={{ y: NESTED_TABLE_SCROLL_HEIGHT }}
          onRow={(record) => ({ onClick: () => toggleKey(record.roleId) })}
          expandable={{
            expandedRowKeys: expandedKeys,
            showExpandColumn: false,
            rowExpandable: () => true,
            expandedRowRender: (record) => (
              <SkillGapForPair
                personId={person.id}
                roleId={record.roleId}
                sri={record.sri}
                currentRoleId={person.currentRoleId}
              />
            ),
          }}
        />
      ) : (
        <Text type="secondary">No matched roles for this person yet.</Text>
      )}
    </NestedTableWrapperStyled>
  );
};

